import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Spin, Card, Modal, Form, TreeSelect, Input, DatePicker, message, Drawer, Descriptions } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getProjects, getNCRs, getContractors, getActivities,
  getIssueTypes, getSeverityList, getHierarchy, createNCR
} from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface NCR {
  id: string;
  workflow_object: string;
  ncr_category: string;
  defect_description: string;
  rectification_required?: string;
  rectification_deadline: string;
  created_at: string;
}

interface WorkflowObject {
  id: string;
  object_id: string;
  title: string;
  status: string;
  severity: string;
  created_at: string;
}

interface Project {
  id: string;
  code: string;
  name: string;
}

const severityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL': return 'red';
    case 'MAJOR': return 'orange';
    case 'MINOR': return 'green';
    default: return 'default';
  }
};

const statusColor = (status: string) => {
  switch (status) {
    case 'CREATED': return 'blue';
    case 'ASSIGNED': return 'cyan';
    case 'UNDER_REVIEW': return 'purple';
    case 'APPROVED': return 'green';
    case 'CLOSED': return 'default';
    case 'REJECTED': return 'red';
    default: return 'default';
  }
};

const buildTreeData = (nodes: any[]): any[] =>
  nodes.map(node => ({
    title: `${node.name} (${node.code})`,
    value: node.id,
    key: node.id,
    children: node.children && node.children.length > 0 ? buildTreeData(node.children) : undefined,
  }));

const NCRPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [ncrs, setNcrs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [contractors, setContractors] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [issueTypes, setIssueTypes] = useState<any[]>([]);
  const [severities, setSeverities] = useState<string[]>([]);
  const [treeData, setTreeData] = useState<any[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNCR, setSelectedNCR] = useState<any>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadNCRs(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadNCRs = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getNCRs(projectId);
      const workflowResponse = await import('../services/api').then(api =>
        api.getWorkflowObjects(projectId, { type: 'NCR' })
      );
      const workflowMap: { [key: string]: WorkflowObject } = {};
      workflowResponse.data.forEach((wo: WorkflowObject) => {
        workflowMap[wo.id] = wo;
      });

      const enrichedNCRs = response.data.map((ncr: NCR) => ({
        ...ncr,
        workflow: workflowMap[ncr.workflow_object] || {},
      }));
      setNcrs(enrichedNCRs);
    } catch (err) {
      setError('Failed to load NCRs.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadNCRs(value);
  };

  const openModal = async () => {
    form.resetFields();
    setModalOpen(true);
    try {
      const [contractorsRes, activitiesRes, issueTypesRes, severityRes, hierarchyRes] = await Promise.all([
        getContractors(),
        getActivities(selectedProject),
        getIssueTypes(),
        getSeverityList(),
        getHierarchy(selectedProject),
      ]);
      setContractors(contractorsRes.data);
      setActivities(activitiesRes.data);
      setIssueTypes(issueTypesRes.data.filter((it: any) => it.applicable_to === 'NCR'));
      const uniqueSeverities: string[] = Array.from(new Set(severityRes.data.map((s: any) => s.severity as string)));
      setSeverities(uniqueSeverities);
      setTreeData(buildTreeData(hierarchyRes.data));
    } catch (err) {
      message.error('Failed to load form data. Please try again.');
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        title: values.title,
        location_id: values.location_id,
        contractor_id: values.contractor_id,
        activity_id: values.activity_id,
        ncr_category: values.ncr_category,
        defect_description: values.defect_description,
        rectification_required: values.rectification_required,
        rectification_deadline: values.rectification_deadline ? values.rectification_deadline.format('YYYY-MM-DD') : null,
        severity: values.severity,
      };
      await createNCR(selectedProject, payload);
      message.success('NCR raised successfully.');
      setModalOpen(false);
      loadNCRs(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.detail || 'Failed to raise NCR.';
      message.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const openDrawer = (record: any) => {
    setSelectedNCR(record);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedNCR(null);
  };

  const columns = [
    {
      title: 'NCR ID',
      dataIndex: ['workflow', 'object_id'],
      key: 'object_id',
      render: (text: string) => <Text strong style={{ color: '#1B2A4A' }}>{text}</Text>,
    },
    {
      title: 'Title',
      dataIndex: ['workflow', 'title'],
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Severity',
      dataIndex: ['workflow', 'severity'],
      key: 'severity',
      render: (severity: string) => (
        <Tag color={severityColor(severity)} icon={<ExclamationCircleOutlined />}>
          {severity}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: ['workflow', 'status'],
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColor(status)}>
          {status?.replace('_', ' ')}
        </Tag>
      ),
    },
    {
      title: 'Deadline',
      dataIndex: 'rectification_deadline',
      key: 'rectification_deadline',
      render: (date: string) => date ? new Date(date).toLocaleDateString() : '—',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          NCR Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Raise NCR
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={ncrs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No NCRs found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title={selectedNCR?.workflow?.object_id || 'NCR Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={480}
      >
        {selectedNCR && (
          <>
            <div style={{ marginBottom: 16 }}>
              <Tag color={severityColor(selectedNCR.workflow?.severity)} icon={<ExclamationCircleOutlined />}>
                {selectedNCR.workflow?.severity}
              </Tag>
              <Tag color={statusColor(selectedNCR.workflow?.status)}>
                {selectedNCR.workflow?.status?.replace('_', ' ')}
              </Tag>
            </div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Title">{selectedNCR.workflow?.title || '—'}</Descriptions.Item>
              <Descriptions.Item label="NCR Category">{selectedNCR.ncr_category || '—'}</Descriptions.Item>
              <Descriptions.Item label="Defect Description">{selectedNCR.defect_description || '—'}</Descriptions.Item>
              <Descriptions.Item label="Rectification Required">{selectedNCR.rectification_required || '—'}</Descriptions.Item>
              <Descriptions.Item label="Deadline">
                {selectedNCR.rectification_deadline ? new Date(selectedNCR.rectification_deadline).toLocaleDateString() : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {selectedNCR.created_at ? new Date(selectedNCR.created_at).toLocaleDateString() : '—'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>

      <Modal
        title="Raise NCR"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Submit NCR"
        width={640}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input placeholder="Brief description of the non-conformance" />
          </Form.Item>

          <Form.Item
            name="location_id"
            label="Location"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <TreeSelect
              treeData={treeData}
              placeholder="Select location"
              treeDefaultExpandAll
              style={{ width: '100%' }}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="contractor_id"
              label="Contractor"
              rules={[{ required: true, message: 'Contractor is required' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select contractor"
                options={contractors.map(c => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
            <Form.Item
              name="activity_id"
              label="Activity"
              rules={[{ required: true, message: 'Activity is required' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select activity"
                options={activities.map(a => ({ value: a.id, label: a.name }))}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="ncr_category"
              label="NCR Category"
              rules={[{ required: true, message: 'Category is required' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select category"
                options={issueTypes.map(it => ({ value: it.id, label: it.name }))}
              />
            </Form.Item>
            <Form.Item
              name="severity"
              label="Severity"
              rules={[{ required: true, message: 'Severity is required' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select severity"
                options={severities.map(s => ({ value: s, label: s }))}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="defect_description"
            label="Defect Description"
            rules={[{ required: true, message: 'Defect description is required' }]}
          >
            <TextArea rows={3} placeholder="Clear description of the non-conformance" />
          </Form.Item>

          <Form.Item
            name="rectification_required"
            label="Rectification Required"
            rules={[{ required: true, message: 'Rectification details are required' }]}
          >
            <TextArea rows={2} placeholder="What rectification is required" />
          </Form.Item>

          <Form.Item name="rectification_deadline" label="Rectification Deadline">
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NCRPage;